import type {
  CatalogProduct,
  CatalogProductPurchasabilityReason,
  CatalogProductStockPolicy,
} from './types'

export type ResolvedProductPurchasability = {
  isPurchasable: boolean
  reason: CatalogProductPurchasabilityReason | null
  stockPolicy: CatalogProductStockPolicy
  availableStock: number
}

const resolveReasonFromProductState = ({
  status,
  name,
  slug,
  hasValidPrice,
  stockPolicy,
  availableStock,
}: {
  status: CatalogProduct['status']
  name: string
  slug: string
  hasValidPrice: boolean | undefined
  stockPolicy: CatalogProductStockPolicy
  availableStock: number
}): CatalogProductPurchasabilityReason | null => {
  if (status !== 'active') return 'status_not_active'
  if (!name.trim() || !slug.trim()) return 'missing_required_fields'
  if (hasValidPrice === false) return 'invalid_price'
  if (stockPolicy === 'deny-oversell' && availableStock <= 0) return 'out_of_stock'
  return null
}

export const resolveCatalogProductPurchasability = (
  product: CatalogProduct | null | undefined,
): ResolvedProductPurchasability => {
  const stockPolicy = product?.purchasability?.stockPolicy ?? product?.stockPolicy ?? 'deny-oversell'
  const parentAvailableStock = Math.max(
    0,
    Math.trunc(product?.purchasability?.availableStock ?? product?.availableStock ?? product?.quantity ?? 0),
  )
  const variants = product?.variants ?? []
  const variantAvailableStock = variants.length
    ? variants.reduce((total, variant) => {
        if (variant.status === 'draft' || variant.status === 'archived') return total
        return total + Math.max(0, Math.trunc(variant.quantity ?? 0))
      }, 0)
    : null
  // A variant product owns its inventory on the variants. Do not let a stale
  // parent quantity/purchasability projection hide stock that is still sold.
  const availableStock = Math.max(0, variantAvailableStock ?? parentAvailableStock)
  const fallbackReason = resolveReasonFromProductState({
    status: product?.status,
    name: product?.name ?? '',
    slug: product?.slug ?? '',
    hasValidPrice: product?.purchasability?.hasValidPrice,
    stockPolicy,
    availableStock,
  })

  const contractIsPurchasable = product?.purchasability?.isPurchasable ?? product?.isPurchasable
  const contractReason = product?.purchasability?.reason
  const stockBlocksPurchase = stockPolicy === 'deny-oversell' && availableStock <= 0
  const shouldReconcileVariantStock = variantAvailableStock !== null && contractReason === 'out_of_stock'
  const isPurchasable = stockBlocksPurchase
    ? false
    : shouldReconcileVariantStock
      ? fallbackReason === null
      : typeof contractIsPurchasable === 'boolean'
        ? contractIsPurchasable
        : fallbackReason === null
  const reason = shouldReconcileVariantStock
    ? (isPurchasable ? null : fallbackReason)
    : product?.purchasability?.reason ?? (isPurchasable ? null : fallbackReason)

  return {
    isPurchasable,
    reason,
    stockPolicy,
    availableStock,
  }
}

export const purchasabilityMessageByReason: Record<CatalogProductPurchasabilityReason, string> = {
  status_not_active: 'Prodotto non pubblicato',
  missing_required_fields: 'Prodotto non acquistabile: dati incompleti',
  invalid_price: 'Prodotto senza prezzo acquistabile',
  out_of_stock: 'Prodotto non disponibile a stock',
}
